<script setup lang="ts">
/**
 * PresenceIndicator — indicador visual do estado de presença.
 *
 * Constitucional:
 * - Princípio XI (Companion ≠ LLM): sem "AI"/"bot"/"online".
 * - CC-01..08: dot discreto, sem contador de tempo (zero streak).
 *
 * Rótulo acessível via aria-label derivado do estado.
 */
import { computed } from "vue";
import { usePresence } from "../composables/usePresence";
import type { PresenceState } from "../types";

interface Props {
  /** Estado a exibir; default = do store. */
  state?: PresenceState;
  /** Tamanho do dot em px. */
  size?: number;
}

const props = withDefaults(defineProps<Props>(), {
  state: undefined,
  size: 10,
});

const presence = usePresence();
// storeToRefs retorna Refs. Usamos .value explicitamente em script setup.
const current = computed<PresenceState>(() => props.state ?? presence.state.value);

const label = computed<string>(() => {
  switch (current.value) {
    case "active":
      return "Presença ativa";
    case "idle":
      return "Presença em pausa";
    case "away":
      return "Presença ausente";
    case "dnd":
      return "Não perturbe";
  }
});

const color = computed<string>(() => {
  switch (current.value) {
    case "active":
      return "var(--hlos-color-success)";       // #4E6E41 (AA 5.32:1)
    case "idle":
      return "var(--hlos-color-warning)";       // #E8A33D (decorativo, ícones)
    case "away":
      return "var(--hlos-color-neutral-400)";  // #8E887E (AA-large 3.5:1)
    case "dnd":
      return "var(--hlos-color-error)";         // #993E2C (AA 6.27:1)
  }
});
</script>

<template>
  <span
    class="hlos-presence"
    :style="{
      display: 'inline-block',
      width: `${props.size}px`,
      height: `${props.size}px`,
      borderRadius: '50%',
      backgroundColor: color,
      verticalAlign: 'middle',
    }"
    :aria-label="label"
    :data-state="current"
    data-testid="presence-indicator"
    role="status"
  />
</template>
